import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Admin, Role, Permission } from '../types';
import { useQuery } from 'react-query';
import { apiClient } from '../lib/api';
import toast from 'react-hot-toast';
import { translatePermission, translateRole } from '../utils/translations';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: Admin;
  onSave: (admin: Partial<Admin>) => Promise<void>;
}

const AdminModal: React.FC<AdminModalProps> = ({ isOpen, onClose, admin: editingAdmin, onSave }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_active: true,
    role_ids: [] as string[],
    permission_ids: [] as string[],
  });

  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [allCategoriesAccess, setAllCategoriesAccess] = useState(false);
  const { data: rolesData, error: rolesError } = useQuery(
    ['roles'],
    () => apiClient.getRoles({ limit: 100 }),
    { enabled: isOpen }
  );

  const { admin } = useAuth();
  const canViewPermissions = hasPermission(admin, 'permissions', 'read');
  const { data: permissionsData, error: permissionsError } = useQuery(
    ['permissions'],
    () => apiClient.getPermissions({ limit: 100 }),
    { enabled: isOpen && canViewPermissions }
  );

  const { data: categoriesData, error: categoriesError } = useQuery(['categories', 'for-admin-modal'], () => apiClient.getCategories({ limit: 100 }), { enabled: isOpen });

  useEffect(() => {
    if (editingAdmin) {
      setFormData({
        username: editingAdmin.username,
        email: editingAdmin.email,
        password: '',
        is_active: editingAdmin.is_active,
        role_ids: editingAdmin.roles.map(r => r.id),
        permission_ids: editingAdmin.permissions.map(p => p.id),
      });
      setCategoryIds(editingAdmin.categories?.map(c => c.id) || []);
      setAllCategoriesAccess(editingAdmin.all_categories_access || false);
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        is_active: true,
        role_ids: [],
        permission_ids: [],
      });
      setCategoryIds([]);
      setAllCategoriesAccess(false);
    }
  }, [editingAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Если это редактирование и пароль пустой, не отправляем его
      const dataToSend = editingAdmin && !formData.password 
        ? { ...formData, password: undefined, category_ids: categoryIds, all_categories_access: allCategoriesAccess }
        : { ...formData, category_ids: categoryIds, all_categories_access: allCategoriesAccess };
      
      await onSave(dataToSend);
      toast.success(editingAdmin ? 'Администратор обновлен успешно' : 'Администратор создан успешно');
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения администратора');
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const handlePermissionToggle = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    setCategoryIds(prev => prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {editingAdmin ? 'Редактировать администратора' : 'Создать администратора'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Имя пользователя
              </label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Пароль {editingAdmin && <span className="text-gray-500">(оставьте пустым, чтобы не изменять)</span>}
              </label>
              <input
                type="password"
                required={!editingAdmin}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Активен
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Роли
              </label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                {rolesError && (rolesError as AxiosError)?.response?.status === 403 ? (
                  <div className="text-sm text-red-600 py-2">Нет прав для просмотра ролей</div>
                ) : !rolesData?.data ? (
                  <div className="text-sm text-gray-500 py-2">Загрузка ролей...</div>
                ) : rolesData.data.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">Нет доступных ролей</div>
                ) : (
                  rolesData.data.map((role) => (
                    <label key={role.id} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={formData.role_ids.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{translateRole(role)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {canViewPermissions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Индивидуальные права (перекрывают права ролей)
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {permissionsError && (permissionsError as AxiosError)?.response?.status === 403 ? (
                    <div className="text-sm text-red-600 py-2">Нет прав для просмотра прав</div>
                  ) : !permissionsData?.data ? (
                    <div className="text-sm text-gray-500 py-2">Загрузка прав...</div>
                  ) : permissionsData.data.length === 0 ? (
                    <div className="text-sm text-gray-500 py-2">Нет доступных прав</div>
                  ) : (
                    permissionsData.data.map((permission) => (
                      <label key={permission.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={formData.permission_ids.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{translatePermission(permission)}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Категории доступа</label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                {categoriesError && (categoriesError as AxiosError)?.response?.status === 403 ? (
                  <div className="text-sm text-red-600 py-2">Нет прав для просмотра категорий</div>
                ) : !categoriesData?.data ? (
                  <div className="text-sm text-gray-500 py-2">Загрузка категорий...</div>
                ) : categoriesData.data.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">Нет доступных категорий</div>
                ) : (
                  categoriesData.data.map((cat) => (
                    <label key={cat.id} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={categoryIds.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        disabled={allCategoriesAccess}
                      />
                      <span className="text-sm text-gray-700">{cat.name}</span>
                    </label>
                  ))
                )}
              </div>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="all_categories_access"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={allCategoriesAccess}
                  onChange={e => setAllCategoriesAccess(e.target.checked)}
                />
                <label htmlFor="all_categories_access" className="ml-2 block text-sm text-gray-900">
                  Доступ ко всем категориям
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                {editingAdmin ? 'Обновить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminModal; 